import { useContext, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { ReactNode } from 'react'
import { DeckContext } from './SlideDeck'
import { flattenChildren } from './slideUtils'
import type { Slide } from './slides/types'

interface ScrollViewerProps {
  children?: ReactNode
  /** Hand-crafted path (ADR-0018). */
  slides?: Slide[]
}

export function ScrollViewer({ children, slides: handCraftedSlides }: ScrollViewerProps) {
  const { moduleTitle, onExit } = useContext(DeckContext)
  // flattenChildren must run during render (not in useMemo) because it may
  // call MDX's _createMdxContent function, which internally uses useContext.
  const slides = handCraftedSlides
    ? handCraftedSlides.map((s) => s.render())
    : flattenChildren(children)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    document.body.classList.add('slide-deck-active')
    return () => document.body.classList.remove('slide-deck-active')
  }, [])

  const deckHref = (() => {
    const params = new URLSearchParams(searchParams)
    params.delete('view')
    const qs = params.toString()
    return qs ? `?${qs}` : ''
  })()

  return (
    <div className="slide-scroll" role="region" aria-label={`${moduleTitle} content`}>
      <header className="slide-chrome">
        <div className="slide-chrome__left">
          {onExit && (
            <button
              type="button"
              onClick={onExit}
              aria-label="Exit module"
              className="slide-chrome__exit"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M3 3l8 8M11 3l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          <Link to={deckHref} className="slide-chrome__scroll-link">
            <span className="slide-chrome__scroll-link-long">View as deck</span>
            <span className="slide-chrome__scroll-link-short" aria-hidden="true">Deck</span>
          </Link>
        </div>
        <div className="slide-chrome__title">{moduleTitle}</div>
        <div className="slide-chrome__right" aria-hidden="true" />
      </header>

      <div className="slide-scroll__body">
        {slides.map((slide, i) => (
          <section
            key={handCraftedSlides?.[i]?.id ?? i}
            data-slide-scroll
            className="slide-scroll__slide"
          >
            {slide}
          </section>
        ))}
      </div>
    </div>
  )
}
